// Please keep files in back/src/api/compute-field updated

import { SupportedLocale } from "@/i18n";
import { ComponentProps, ReactNode } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import {
  ArtworkDescriptionEnsembleFixeCF,
  ArtworkDescriptionEnsembleTemporaireCf,
  ArtworkDimensionsCadreCF,
  ArtworkDimensionsCF,
  ArtworkDimensionsHorsMargesCF,
  ArtworkLocalisationHistoriqueCF,
  ArtworkMainTitleCF,
  ArtworkOverviewSummaryCF,
  ArtworkRelatedAuthorsCF,
  ArtworkSignatureCF,
  ArtworkSummaryCF,
  ArtworkSummaryPhotographieMasterCF,
  ArtworkSupportTechniqueCF,
  UnstyledArtworkTitleCF,
} from "./compute-field/artwork";
import { RelatedAuctionsCF } from "./compute-field/artwork-in-auction";
import { RelatedBibliographiesCF } from "./compute-field/artwork-in-bibliography";
import { RelatedExhibitionsCF } from "./compute-field/artwork-in-exhibition";
import { AuctionSummaryCF } from "./compute-field/auction";
import { AudiovisualSummaryCF } from "./compute-field/audiovisual";
import {
  BibliographieDimensionsCF,
  BibliographieLongSummaryCF,
} from "./compute-field/bibliography";
import { CollectorNameCF, CollectorNameForOverviewCF } from "./compute-field/collector";
import { DocumentSummaryCF } from "./compute-field/document";
import { ExhibitionSummaryCF } from "./compute-field/exposition";
import {
  MultipleprintDimensionImageCF,
  MultipleprintDimensionsCF,
  MultipleprintRelatedAuthorsCF,
  MultipleprintSignatureCF,
  MultipleprintSummaryCF,
  MultipleprintSupportTechniqueCF,
  MultipleprintTitleCF,
} from "./compute-field/multipleprint";
import {
  PhotographyDimensionsCF,
  PhotographySummaryCF,
} from "./compute-field/photographie";
import { DimensionsFormatting } from "./compute-field/shared/dimensions";
import { InscriptionDosCF } from "./compute-field/shared/inscription-dos";
import { DateCF } from "./compute-field/shared/title";
import { TirageHistoryCF } from "./compute-field/tirage";
import { EcritSummaryCF } from "./compute-field/writing";

export type ComputeFieldComponent<T, CustomProps = unknown> = (
  props: {
    input: T;
    lineBreak?: boolean;
    inline?: boolean;
    endWithPeriod?: boolean;
    language: SupportedLocale;
    translation?: (key: string) => string;
  } & CustomProps,
) => ReactNode;

function computeFieldRenderer<T>(
  CFComponent: ComputeFieldComponent<T>,
): (
  input: T,
  params: Omit<ComponentProps<ComputeFieldComponent<T>>, "input">,
) => string {
  return (input: T, params) =>
    renderToStaticMarkup(CFComponent({ input, ...params }));
}

export function unwrapPTag(input: string) {
  return input.replace(/<p[^>]*>(.*?)<\/p>/g, "$1");
}

////////// Artwork
export const computeFieldUnstyledArtwork = computeFieldRenderer(
  UnstyledArtworkTitleCF,
);

export const computeFieldDimensionsArtwork =
  computeFieldRenderer(ArtworkDimensionsCF);

export const computeFieldTitleArtwork =
  computeFieldRenderer(ArtworkMainTitleCF);

export const computeFieldDimensionsHorsMargesArtwork = computeFieldRenderer(
  ArtworkDimensionsHorsMargesCF,
);

export const computeFieldDimensionsCadreArtwork = computeFieldRenderer(
  ArtworkDimensionsCadreCF,
);

export const computeFieldRelatedAuthorsArtwork = computeFieldRenderer(
  ArtworkRelatedAuthorsCF,
);

export const computeFieldSupportTechniqueArtwork = computeFieldRenderer(
  ArtworkSupportTechniqueCF,
);

export const computeFieldSignatureArtwork =
  computeFieldRenderer(ArtworkSignatureCF);

export const computeFieldInscriptionDosArtwork = computeFieldRenderer(
  InscriptionDosCF,
);

export const computeFieldSummaryArtwork =
  computeFieldRenderer(ArtworkSummaryCF);

export const computeFieldSummaryPhotographieMasterArtwork =
  computeFieldRenderer(ArtworkSummaryPhotographieMasterCF);

export const computeFieldArtworkOverviewSummary = <T extends ComponentProps<typeof ArtworkOverviewSummaryCF>["input"]>(
  input: T,
  params: Omit<ComponentProps<typeof ArtworkOverviewSummaryCF>, "input">,
): string => {
  return renderToStaticMarkup(
    ArtworkOverviewSummaryCF({ input, ...params } as ComponentProps<typeof ArtworkOverviewSummaryCF>),
  );
};



export const computeFieldDescriptionEnsembleFixeArtwork = computeFieldRenderer(
  ArtworkDescriptionEnsembleFixeCF,
);

export const computeFieldDescriptionEnsembleTemporaireArtwork =
  computeFieldRenderer(ArtworkDescriptionEnsembleTemporaireCf);

export const computeFieldLocalisationHistoriqueArtwork = computeFieldRenderer(
  ArtworkLocalisationHistoriqueCF,
);

export const computeFieldRelatedExhibitions =
  computeFieldRenderer(RelatedExhibitionsCF);

export const computeFieldRelatedAuctions =
  computeFieldRenderer(RelatedAuctionsCF);

export const computeFieldRelatedBibliographies = computeFieldRenderer(
  RelatedBibliographiesCF,
);

///////// Bibliography

export const computeFieldDimensionsBibliographie = computeFieldRenderer(
  BibliographieDimensionsCF,
);

export const computeFieldLongSummaryBibliographie = computeFieldRenderer(
  BibliographieLongSummaryCF,
);

///////// Collector

export const computeFieldCollectorName = computeFieldRenderer(CollectorNameCF);
export const computeFieldCollectorNameForOverview = computeFieldRenderer(CollectorNameForOverviewCF);

///////// Auction

export const computeFieldAuctionSummary =
  computeFieldRenderer(AuctionSummaryCF);

///////// Multipleprint

export const computeFieldMultipleprintDimensions = computeFieldRenderer(
  MultipleprintDimensionsCF,
);

export const computeFieldMultipleprintDimensionImage = computeFieldRenderer(
  MultipleprintDimensionImageCF,
);

export const computeFieldMultipleprintRelatedAuthors = computeFieldRenderer(
  MultipleprintRelatedAuthorsCF,
);

export const computeFieldMultipleprintSupportTechnique = computeFieldRenderer(
  MultipleprintSupportTechniqueCF,
);

export const computeFieldMultipleprintSignature = computeFieldRenderer(
  MultipleprintSignatureCF,
);

export const computeFieldMultipleprintTitle =
  computeFieldRenderer(MultipleprintTitleCF);

export const computeFieldMultipleprintSummary = computeFieldRenderer(
  MultipleprintSummaryCF,
);

///////// Writing

export const computeFieldWritingDescription =
  computeFieldRenderer(EcritSummaryCF);

///////// Audiovisual

export const computeFieldAudiovisualSummary =
  computeFieldRenderer(AudiovisualSummaryCF);

///////// Document

export const computeFieldDocumentSummary =
  computeFieldRenderer(DocumentSummaryCF);

///////// Exhibition

export const computeFieldExhibitionSummary =
  computeFieldRenderer(ExhibitionSummaryCF);

///////// Photography

export const computeFieldPhotographySummary =
  computeFieldRenderer(PhotographySummaryCF);

export const computeFieldPhotographyDimensions = computeFieldRenderer(
  PhotographyDimensionsCF,
);

export const computeFieldDate = computeFieldRenderer(DateCF);

///////// Tirage (prints)

export const computeFieldTirageDimensions = computeFieldRenderer(
  DimensionsFormatting,
);

export const computeFieldTirageHistory = computeFieldRenderer(
  TirageHistoryCF,
);
